import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import SectionEyebrow from "@/components/common/SectionEyebrow";
import { publicApi } from "@/lib/api";
import { ArrowRight, ArrowUpRight, FileText, Network, Telescope } from "lucide-react";

export default function Research() {
  const [page, setPage] = useState(null);
  const [domains, setDomains] = useState([]);
  useEffect(() => {
    publicApi.page("research").then(setPage).catch(() => {});
    publicApi.domains().then(setDomains).catch(() => {});
  }, []);
  const p = page || {};
  return (
    <PublicLayout title="Research">
      <section className="relative bsca-dark bsca-cosmic bsca-grain -mt-16 md:-mt-20 lg:-mt-[88px] pt-36 md:pt-44 pb-24 overflow-hidden">
        <div className="bsca-field-lines" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-14 bsca-fade-up">
          <SectionEyebrow dark>{p.hero?.eyebrow || "Research"}</SectionEyebrow>
          <h1 className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl">
            {p.hero?.headline}
          </h1>
          <p className="mt-6 text-white/75 text-lg max-w-2xl">{p.hero?.subheadline}</p>
        </div>
      </section>

      <section className="bg-[#F8F6F1] py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <SectionEyebrow>Research Philosophy</SectionEyebrow>
          <p className="font-serif text-2xl md:text-3xl tracking-tight leading-snug">{p.philosophy}</p>
        </div>
      </section>

      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <SectionEyebrow>Research Domains</SectionEyebrow>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight">Five frontiers, one method.</h2>
            </div>
            <Link to="/research-compendium" data-testid="link-research-compendium-from-research" className="bsca-link inline-flex items-center text-sm font-medium">
              View Research Compendium <ArrowUpRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 border border-black/10 bsca-stagger">
            {domains.map((d, i) => (
              <Link
                to={`/research-compendium?domain=${d.slug}`}
                key={d.slug}
                data-testid={`research-domain-card-${d.slug}`}
                className="bg-white p-8 hover:bg-[#F8F6F1] transition-colors group"
              >
                <Telescope className="h-6 w-6 text-[#D4AF37]" strokeWidth={1.4} />
                <div className="mt-5 bsca-eyebrow text-[#7A5A06] text-[10px]">Domain 0{i + 1}</div>
                <h3 className="mt-3 font-serif text-xl">{d.title}</h3>
                <p className="mt-3 text-[#4B5563] leading-relaxed text-sm">{d.description}</p>
                <span className="mt-6 inline-flex items-center text-xs group-hover:text-[#7A5A06]">Explore questions <ArrowRight className="h-3 w-3 ml-1" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F8F6F1] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid md:grid-cols-2 gap-12">
          <div className="bg-white border border-black/10 p-10">
            <Network className="h-7 w-7 text-[#D4AF37]" strokeWidth={1.3} />
            <h3 className="mt-5 font-serif text-2xl">Collaborate on Frontier Research</h3>
            <p className="mt-3 text-[#4B5563]">Researchers, labs, and institutions are invited to co-develop programs and publications.</p>
            <p className="mt-2 text-xs text-[#9CA3AF] italic">{p.partner_note}</p>
            <Link to="/engage#collaboration" data-testid="btn-research-collaborate" className="mt-6 inline-flex items-center bg-[#050B14] text-white px-6 py-3 rounded-sm bsca-lift">Propose collaboration <ArrowRight className="h-4 w-4 ml-2" /></Link>
          </div>
          <div className="bg-white border border-black/10 p-10">
            <FileText className="h-7 w-7 text-[#D4AF37]" strokeWidth={1.3} />
            <h3 className="mt-5 font-serif text-2xl">Whitepapers & Publications</h3>
            <p className="mt-3 text-[#4B5563]">BSCA publishes peer-reviewed papers, essays, and position documents on the frontier questions in its compendium.</p>
            <p className="mt-2 text-xs text-[#9CA3AF] italic">{p.whitepaper_note}</p>
            <Link to="/research-compendium" data-testid="btn-research-compendium" className="mt-6 inline-flex items-center border border-[#050B14] text-[#050B14] px-6 py-3 rounded-sm bsca-lift hover:bg-[#050B14] hover:text-white">Browse compendium</Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
